import Button from '@/components/atoms/Button';
import ExternalLink from '@/components/atoms/ExternalLink';
import Heading from '@/components/atoms/Heading';
import { OptionDefinition } from '@/components/atoms/RadioButton';
import { AnalyticsData } from '@/components/templates/AnalyticsData';
import historyContext from '@/hooks/historyContext';
import useSetLoading from '@/hooks/useSetLoading';
import { hasSurrogatePair } from '@/mobile/utils/common';
import alertState from '@/states/alertState';
import { PasswordResetForm, PasswordResetFormState } from '@/states/passwordResetFormState';
import { OnetimePasswordRequest } from '@/types/api/mypage/onetimePassword';
import api from '@/utils/api';
import { isZenkakuOnly } from '@/utils/check';
import { isExpiredToken } from '@/utils/cognito';
import { toHankaku, toZenkaku } from '@/utils/commonFunc';
import url from '@/utils/config';
import { ApiUrl, Constants } from '@/utils/constants';
import {
  existsBirthDate,
  getBirthDateDayList,
  getBirthDateMonthList,
  getBirthDateYearList,
  zeroPadding,
} from '@/utils/date';
import { isMobilePhoneNumberFormat } from '@/utils/validator/constraints/phone';
import { yupResolver } from '@hookform/resolvers/yup';
import type { NextPage } from 'next';
import Router from 'next/router';
import { ChangeEvent, ReactElement, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRecoilState, useSetRecoilState } from 'recoil';
import * as yup from 'yup';

/**
 * 契約者情報と連絡先を使って本人確認を行い、
 * ワンタイムパスワード送信へ進めるパスワード再設定フローの入口ページ。
 */
const PasswordReset: NextPage = () => {
  const [passwordResetForm, setPasswordResetForm] = useRecoilState(PasswordResetFormState);
  const [birthDateDayPullDown, setBirthDateDayPullDown] = useState<
    { key: number; value: string; label: string }[]
  >(getBirthDateDayList(passwordResetForm.birthDateYear, passwordResetForm.birthDateMonth));
  const history = useContext(historyContext);
  const [serverErrorMessage, setServerErrorMessage] = useState<string>('');
  const { startLoading, finishLoading } = useSetLoading();
  const setAlert = useSetRecoilState(alertState);
  const [isMoreThanOneCustomerFound, setIsMoreThanOneCustomerFound] = useState(false);

  /** メール認証が選択されているかを判定 */
  const isSelectedAuthMail = (): boolean => {
    return (
      passwordResetForm.authenticationMethod === Constants.PasswordReset.AuthenticationMethod.MAIL
    );
  };

  /** SMS認証が選択されているかを判定 */
  const isSelectedAuthSms = (): boolean => {
    return (
      passwordResetForm.authenticationMethod === Constants.PasswordReset.AuthenticationMethod.SMS
    );
  };

  /**
   * バリデーションスキーマ定義
   * - 姓名: 全角のみ・32文字以内
   * - 生年月日: 全項目必須
   * - メール/SMS: 認証方法に応じて条件付き必須
   * - お客さま番号/SIM番号: 複数契約時のみいずれか必須
   */
  const schema = yup.object().shape({
    lsName: yup
      .string()
      .required('姓が未入力です。姓を入力してください。')
      .max(32, '姓は32文字以内で入力してください。')
      .test('invalid', '姓は全角で入力してください。', (value) => {
        const lsName = toZenkaku(value ?? '');
        return isZenkakuOnly(lsName);
      }),
    flName: yup
      .string()
      .required('名が未入力です。名を入力してください。')
      .max(32, '名は32文字以内で入力してください。')
      .test('invalid', '名は全角で入力してください。', (value) => {
        const flName = toZenkaku(value ?? '');
        return isZenkakuOnly(flName);
      }),
    birthDateYear: yup.string().required('生年月日を全て入力してください。'),
    birthDateMonth: yup.string().required('生年月日を全て入力してください。'),
    birthDateDay: yup.string().required('生年月日を全て入力してください。'),
    mailAddress: yup
      .string()
      .test('required', 'メールアドレスを入力してください。', (value) => {
        const mailAddress = toHankaku(value ?? '');
        return !(isSelectedAuthMail() && !mailAddress);
      })
      .test(
        'invalid',
        'メールアドレスの文字列が不正です。入力内容を確認してください。',
        (value) => {
          const mailAddress = toHankaku(value ?? '');
          return !(
            isSelectedAuthMail() &&
            !mailAddress?.match(
              /^[a-zA-Z0-9!#$%&'*+\\\-/=?^_`{}~\\|\\.]+@([a-zA-Z0-9][a-zA-Z0-9-]*\.)+[a-zA-Z0-9][a-zA-Z0-9-]*$/
            )
          );
        }
      )
      .test('max', 'メールアドレスは256文字以内で入力してください。', (value) => {
        const mailAddress = toHankaku(value ?? '');
        return !(isSelectedAuthMail() && mailAddress !== undefined && mailAddress?.length > 256);
      }),
    phoneNumber: yup
      .string()
      .test('required', '連絡先電話番号を入力してください。', (value) => {
        const phoneNumber = toHankaku(value ?? '');
        return !(isSelectedAuthSms() && !phoneNumber);
      })
      .test('invalid', '連絡先電話番号は半角数字11桁で入力してください。', (value) => {
        const phoneNumber = toHankaku(value ?? '');
        return !(isSelectedAuthSms() && !phoneNumber?.match(/^0[0-9]0[0-9]{4}[0-9]{4}$/));
      }),
    ispNumber: yup
      .string()
      .test('required', 'お客さま番号を入力してください。', (value) => {
        const ispNumber = toHankaku(value ?? '');
        if (!isMoreThanOneCustomerFound) {
          return true;
        }
        if (ispNumber !== '' || passwordResetForm.simNumber !== '') {
          return true;
        }
        return false;
      })
      .test('invalid', 'お客さま番号の文字列が不正です。入力内容をご確認ください。', (value) => {
        const ispNumber = toHankaku(value ?? '');
        const pattern = /^([a-zA-Z0-9]{16})$/;
        if (!isMoreThanOneCustomerFound) {
          return true;
        }
        if (ispNumber === '' || passwordResetForm.simNumber !== '') {
          return true;
        }
        if (ispNumber !== '') {
          if (!pattern.test(ispNumber || '')) {
            return false;
          }
        }
        return true;
      }),

    simNumber: yup
      .string()
      .test('required', 'SIM番号を入力してください。', (value) => {
        if (!isMoreThanOneCustomerFound) {
          return true;
        }
        if (value !== '' || passwordResetForm.ispNumber !== '') {
          return true;
        }
        return false;
      })
      .test('invalid', '数字11文字または14文字で入力してください。', (value) => {
        if (!isMoreThanOneCustomerFound) {
          return true;
        }
        if (value === '' || passwordResetForm.ispNumber !== '') {
          return true;
        }
        if (!isMobilePhoneNumberFormat(value)) {
          return false;
        }
        return true;
      }),
  });

  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm<PasswordResetForm>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  /** フォーム入力値をRecoil stateに同期（onChange用） */
  const updatePasswordResetFormInput = (
    e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>
  ) => {
    const { id, value } = e.target;

    // 絵文字などのサロゲートペアはバックエンド側の想定外入力になるため、state へ入れる前に弾く。
    if (hasSurrogatePair(value)) return;

    setPasswordResetForm({ ...passwordResetForm, [id]: value });
    if (id === 'simNumber') {
      clearErrors('ispNumber');
    }
    if (id === 'ispNumber') {
      clearErrors('simNumber');
    }
  };

  /** フォーム入力値をRecoil stateに同期（onBlur用：全角/半角正規化を実施） */
  const updatePasswordResetChangeFormInput = (
    e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>
  ) => {
    const { id, value } = e.target;

    // blur 時に表記を正規化しておくことで、バリデーションと API 送信値のズレを防ぐ。
    if (hasSurrogatePair(value)) return;

    if (id === 'lsName') {
      setPasswordResetForm({ ...passwordResetForm, [id]: toZenkaku(value) });
    } else if (id === 'flName') {
      setPasswordResetForm({ ...passwordResetForm, [id]: toZenkaku(value) });
    } else if (id === 'mailAddress') {
      setPasswordResetForm({ ...passwordResetForm, [id]: toHankaku(value) });
    } else if (id === 'phoneNumber') {
      setPasswordResetForm({ ...passwordResetForm, [id]: toHankaku(value) });
    } else if (id === 'ispNumber') {
      setPasswordResetForm({ ...passwordResetForm, [id]: toHankaku(value) });
    }

    if (id === 'simNumber') {
      clearErrors('ispNumber');
    }
    if (id === 'ispNumber') {
      clearErrors('simNumber');
    }
  };

  /** プルダウン（select）の変更をRecoil stateに反映 */
  const updatePasswordResetFormSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const { id, value } = e.target;
    setPasswordResetForm({ ...passwordResetForm, [id]: value });
  };

  /** 生年月日（年）変更時：日のプルダウン候補をうるう年考慮で再生成 */
  const updateBirthDateYear = (e: ChangeEvent<HTMLSelectElement>) => {
    updatePasswordResetFormSelect(e);

    resetBirthDateDay(
      e.target.value,
      passwordResetForm.birthDateMonth,
      passwordResetForm.birthDateDay
    );
  };

  /** 生年月日（月）変更時：日のプルダウン候補を再生成 */
  const updateBirthDateMonth = (e: ChangeEvent<HTMLSelectElement>) => {
    updatePasswordResetFormSelect(e);

    resetBirthDateDay(
      passwordResetForm.birthDateYear,
      e.target.value,
      passwordResetForm.birthDateDay
    );
  };

  /** 年月変更に応じて日候補を再生成し、存在しない日が残らないようにリセットする */
  const resetBirthDateDay = (
    _birthDateYear: string | undefined,
    _birthDateMonth: string | undefined,
    _birthDateDay: string | undefined
  ) => {
    // 年月の変更に合わせて日候補を再生成し、存在しない日付が残らないようにする。
    setBirthDateDayPullDown(getBirthDateDayList(_birthDateYear, _birthDateMonth));

    if (
      _birthDateYear &&
      _birthDateMonth &&
      _birthDateDay &&
      !existsBirthDate(_birthDateYear, _birthDateMonth, _birthDateDay)
    ) {
      setPasswordResetForm({
        ...passwordResetForm,
        birthDateYear: _birthDateYear,
        birthDateMonth: _birthDateMonth,
        birthDateDay: '',
      });
    }
  };

  useEffect(() => {
    // ログイン画面やフロー入口から来たときだけ完全初期化し、戻る遷移では入力途中の情報を復元する。
    if (history[0].includes('login') || history[0] === '/password_reset/') {
      setPasswordResetForm({
        ...passwordResetForm,
        lsName: '',
        flName: '',
        birthDateYear: '',
        birthDateMonth: '',
        birthDateDay: '',
        authenticationMethod: Constants.PasswordReset.AuthenticationMethod.MAIL,
        mailAddress: '',
        phoneNumber: '',
        ispNumber: '',
        simNumber: '',
      });
      setIsMoreThanOneCustomerFound(false);
    } else {
      setPasswordResetForm({
        ...passwordResetForm,
        lsName: passwordResetForm.lsName,
        flName: passwordResetForm.flName,
        birthDateYear: passwordResetForm.birthDateYear,
        birthDateMonth: passwordResetForm.birthDateMonth,
        birthDateDay: passwordResetForm.birthDateDay,
        authenticationMethod:
          passwordResetForm.authenticationMethod ||
          Constants.PasswordReset.AuthenticationMethod.MAIL,
        mailAddress: isSelectedAuthMail() ? passwordResetForm.mailAddress : '',
        phoneNumber: isSelectedAuthSms() ? passwordResetForm.phoneNumber : '',
        ispNumber: passwordResetForm.ispNumber,
        simNumber: passwordResetForm.simNumber,
      });

      if (passwordResetForm.ispNumber !== '') {
        setIsMoreThanOneCustomerFound(true);
      } else if (passwordResetForm.simNumber !== '') {
        setIsMoreThanOneCustomerFound(true);
      } else {
        setIsMoreThanOneCustomerFound(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 生年月日をAPI送信用のYYYY-MM-DD形式に整形 */
  const getBirthDateParam = (): string => {
    // API 仕様に合わせて分割入力した年月日を連結する。
    return `${passwordResetForm.birthDateYear}-${zeroPadding(
      Number(passwordResetForm.birthDateMonth),
      2
    )}-${zeroPadding(Number(passwordResetForm.birthDateDay), 2)}`;
  };

  /** 認証方法のUI選択値をAPI列挙値（EMAIL/SMS）に変換 */
  const getAuthenticationMethodParam = (): string => {
    // 画面の選択値を API 側の列挙値へ寄せて、送信フォーマットを UI 実装から独立させる。
    if (isSelectedAuthMail()) {
      return 'EMAIL';
    }
    if (isSelectedAuthSms()) {
      return 'SMS';
    }
    return '';
  };

  /** ワンタイムパスワードAPI送信用のリクエストパラメータを組み立て */
  const getOnetimePasswordRequestParam = (): OnetimePasswordRequest => {
    // 契約が複数ある場合だけ補助識別子を付け、通常ケースでは不要な条件を送らないようにする。
    const param: OnetimePasswordRequest = {
      ls_name: passwordResetForm.lsName,
      fl_name: passwordResetForm.flName,
      birthday: getBirthDateParam(),
      authway: getAuthenticationMethodParam(),
      isp_cn_no:
        isMoreThanOneCustomerFound && passwordResetForm.ispNumber !== ''
          ? passwordResetForm.ispNumber
          : undefined,
      sim_phone_number:
        isMoreThanOneCustomerFound && passwordResetForm.simNumber !== ''
          ? passwordResetForm.simNumber
          : undefined,
    };

    if (isSelectedAuthMail()) {
      param.mailaddress = passwordResetForm.mailAddress;
    } else {
      param.tel = passwordResetForm.phoneNumber;
    }

    return param;
  };

  /**
   * 認証実行ハンドラ
   * ワンタイムパスワードAPIを呼び出し、成功時はOTP入力画面へ遷移する。
   * エラーコード別に送信上限・複数契約の分岐を行う。
   */
  const authenticate = () => {
    // 選択していない認証経路の値を落としてから送信し、不要な情報で判定がぶれないようにする。
    setPasswordResetForm({
      ...passwordResetForm,
      mailAddress: isSelectedAuthMail() ? passwordResetForm.mailAddress : '',
      phoneNumber: isSelectedAuthSms() ? passwordResetForm.phoneNumber : '',
    });

    (async () => {
      startLoading(ApiUrl.NonProtected.ONETIME_PASSWORD_V2);
      await api
        .post(ApiUrl.NonProtected.ONETIME_PASSWORD_V2, getOnetimePasswordRequestParam(), {
          headers: { 'content-type': 'application/json' },
        })
        .then(() => {
          Router.push({
            pathname: '/password_reset/onetime_password/',
          });
        })
        .catch((error) => {
          if (isExpiredToken(error)) {
            sessionStorage.clear();
            window.location.href = url('/login');
            return;
          }
          const errorCode = error.response.data?.error_code;

          if (errorCode === 'TOO_MANY_ATTEMPTS') {
            setServerErrorMessage('ワンタイムパスワード送信上限オーバー');
          } else if (errorCode === 'MORE_THAN_ONE_CUSTOMER_FOUND') {
            // 契約が複数見つかった場合だけ追加識別子の入力欄を開き、通常導線の入力負荷を増やさない。
            setIsMoreThanOneCustomerFound(true);
          } else {
            setAlert({
              isOpen: true,
              apiType: '',
            });
          }
        })
        .finally(() => finishLoading(ApiUrl.NonProtected.ONETIME_PASSWORD_V2));
    })();
  };

  /**
   * 認証方法ラジオボタンのレンダリング
   * 共通RadioButton部品ではなく専用実装を使用（認証方法に連動した入力欄の活性制御が必要なため）
   */
  const authenticationMethodComponent = (): React.JSX.Element => {
    // この画面は認証方法ごとに入力欄の活性制御があるため、専用実装で state と密結合させている。
    const options = [
      {
        label: 'メールアドレス',
        value: Constants.PasswordReset.AuthenticationMethod.MAIL,
      },
      {
        label: '携帯電話（SMS）',
        value: Constants.PasswordReset.AuthenticationMethod.SMS,
      },
    ];
    return (
      <ul className='m-form-radio'>
        {options.map((option: OptionDefinition): ReactElement => {
          return (
            <li key={option.value} className='m-form-radio__item'>
              <label>
                <input
                  type='radio'
                  id='authenticationMethod'
                  className='m-form-radio__input'
                  value={option.value}
                  checked={option.value === passwordResetForm.authenticationMethod}
                  {...register('authenticationMethod')}
                  onChange={updatePasswordResetFormInput}
                />
                {option.label}
              </label>
            </li>
          );
        })}
      </ul>
    );
  };

  /** バリデーションエラーメッセージの表示コンポーネント */
  const errorMessageComponent = (errorMessage: string | undefined): React.JSX.Element => {
    return (
      <p className='m-text' role='alert' aria-live='assertive'>
        <strong>{errorMessage}</strong>
      </p>
    );
  };

  return (
    <>
      <AnalyticsData />
      <main className='l-main'>
        <Heading as='h1'>ユーザーIDの確認とパスワードの再設定</Heading>
        <h2>ご契約者様情報の入力</h2>
        <p className='m-text'>
          ご契約者様の氏名を入力し、認証方法を選択してください。
          <br />
          認証方法によって入力項目が異なります。
          <br />
          必要情報を入力後に「次へ」ボタンから認証に進んでください。
        </p>
        {/* 認証方法で選択した情報を入力する旨の注意書き */}
        <p className='m-list-notice__item text-alert' style={{ marginBottom: '1.5rem' }}>
          認証方法で選択した情報を入力してください。
        </p>
        {serverErrorMessage && errorMessageComponent(serverErrorMessage)}
        <div className='m-form__item m-form__item--name'>
          <div className='m-form__title'>
            <span className='m-form__titleTxt'>氏名</span>
            <span className='m-form__label'>必須</span>
          </div>
          <div className='error-messages'>
            {errors.lsName && errorMessageComponent(errors.lsName.message)}
            {errors.flName && errorMessageComponent(errors.flName.message)}
          </div>
          <div className='m-form__lyt'>
            <div className='m-form__lytItem'>
              <label htmlFor='lsName' className='m-form__txt' style={{ whiteSpace: 'nowrap' }}>
                姓
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type='text'
                  id='lsName'
                  className={`m-form-text ${errors.lsName && 'is-error'}`}
                  value={passwordResetForm.lsName}
                  aria-label='姓（例：山田）'
                  {...register('lsName', {
                    onChange: (e) => {
                      updatePasswordResetFormInput(e);
                    },
                    onBlur: (e) => {
                      updatePasswordResetChangeFormInput(e);
                    },
                  })}
                />
                <span
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    color: '#909090',
                    fontSize: '1.4rem',
                  }}
                >
                  例：山田
                </span>
              </div>
            </div>
            <div className='m-form__lytItem'>
              <div
                className='m-form__lytItem'
                style={{ display: 'flex', alignItems: 'center', position: 'relative' }}
              >
                <label htmlFor='flName' className='m-form__txt' style={{ whiteSpace: 'nowrap' }}>
                  名
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type='text'
                    id='flName'
                    className={`m-form-text ${errors.flName && 'is-error'}`}
                    value={passwordResetForm.flName}
                    aria-label='名（例：太郎）'
                    {...register('flName', {
                      onChange: (e) => {
                        updatePasswordResetFormInput(e);
                      },
                      onBlur: (e) => {
                        updatePasswordResetChangeFormInput(e);
                      },
                    })}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      color: '#909090',
                      fontSize: '1.4rem',
                    }}
                  >
                    例：太郎
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='m-form__item m-form__item--year'>
          <div className='m-form__title'>
            <span className='m-form__titleTxt'>生年月日(年/月/日)</span>
            <span className='m-form__label'>必須</span>
          </div>
          {errors.birthDateYear && errorMessageComponent(errors.birthDateYear.message)}
          {!errors.birthDateYear &&
            errors.birthDateMonth &&
            errorMessageComponent(errors.birthDateMonth.message)}
          {!errors.birthDateYear &&
            !errors.birthDateMonth &&
            errors.birthDateDay &&
            errorMessageComponent(errors.birthDateDay.message)}
          <div className='m-form__lyt'>
            <label className='m-form__lytItem'>
              <select
                id='birthDateYear'
                className={`m-form-select ${errors.birthDateYear && 'is-error birth-date'}`}
                value={passwordResetForm.birthDateYear}
                {...register('birthDateYear')}
                onChange={updateBirthDateYear}
              >
                <option value='' disabled>
                  &nbsp;
                </option>
                {getBirthDateYearList().map((year: any) => (
                  <option key={year.key} value={year.value}>
                    {year.label}
                  </option>
                ))}
              </select>
              <span className='m-form__txt'>年</span>
            </label>
            <label className='m-form__lytItem'>
              <select
                id='birthDateMonth'
                className={`m-form-select ${errors.birthDateMonth && 'is-error birth-date'}`}
                value={passwordResetForm.birthDateMonth}
                {...register('birthDateMonth')}
                onChange={updateBirthDateMonth}
              >
                <option value='' disabled>
                  &nbsp;
                </option>
                {getBirthDateMonthList().map((month) => (
                  <option key={month} value={`${month}`}>
                    {month}
                  </option>
                ))}
              </select>
              <span className='m-form__txt'>月</span>
            </label>
            <label className='m-form__lytItem'>
              <select
                id='birthDateDay'
                className={`m-form-select ${errors.birthDateDay && 'is-error birth-date'}`}
                value={passwordResetForm.birthDateDay}
                {...register('birthDateDay')}
                onChange={updatePasswordResetFormSelect}
              >
                <option value='' disabled>
                  &nbsp;
                </option>
                {birthDateDayPullDown.map((day: any) => (
                  <option key={day.key} value={day.value}>
                    {day.label}
                  </option>
                ))}
              </select>
              <span className='m-form__txt'>日</span>
            </label>
          </div>
        </div>
        <div className='m-form__item'>
          <div className='m-form__title'>
            <span className='m-form__titleTxt'>ワンタイムパスワード認証方法</span>
            <span className='m-form__label'>必須</span>
          </div>
          {authenticationMethodComponent()}
        </div>
        <div className='m-form__item'>
          <label htmlFor='mailAddress' className='m-form__title'>
            <span className='m-form__titleTxt'>
              連絡先メールアドレス(※ご登録済みのものに限ります)
            </span>
            <span className='m-form__label'>必須</span>
          </label>
          {errors.mailAddress && errorMessageComponent(errors.mailAddress.message)}
          <div className='m-form__lyt'>
            <input
              type='email'
              id='mailAddress'
              className={`m-form-text ${errors.mailAddress && 'is-error'}`}
              value={passwordResetForm.mailAddress}
              {...register('mailAddress', {
                onChange: (e) => {
                  updatePasswordResetFormInput(e);
                },
                onBlur: (e) => {
                  updatePasswordResetChangeFormInput(e);
                },
              })}
              disabled={
                passwordResetForm.authenticationMethod !==
                Constants.PasswordReset.AuthenticationMethod.MAIL
              }
            />
          </div>
        </div>
        <div className='m-form__item'>
          <label htmlFor='phoneNumber' className='m-form__title'>
            <span className='m-form__titleTxt'>
              連絡先電話番号(※ご登録済みのもので携帯電話番号に限ります)
            </span>
            <span className='m-form__label'>必須</span>
          </label>
          {errors.phoneNumber && errorMessageComponent(errors.phoneNumber.message)}
          <div className='m-form__lyt'>
            <input
              type='text'
              id='phoneNumber'
              className={`m-form-text ${errors.phoneNumber && 'is-error'}`}
              value={passwordResetForm.phoneNumber}
              {...register('phoneNumber', {
                onChange: (e) => {
                  updatePasswordResetFormInput(e);
                },
                onBlur: (e) => {
                  updatePasswordResetChangeFormInput(e);
                },
              })}
              disabled={
                !(
                  passwordResetForm.authenticationMethod ===
                  Constants.PasswordReset.AuthenticationMethod.SMS
                )
              }
            />
          </div>
        </div>
        {isMoreThanOneCustomerFound && (
          <div>
            <div className='m-form__item'>
              <label htmlFor='ispNumber' className='m-form__title'>
                <span className='m-list-notice__item text-alert'>
                  お客様は複数のご契約をお持ちのため、ご希望の契約のお客さま番号、またはSIM電話番号をご入力ください。
                </span>
                <span className='m-form__label'>いずれか必須</span>
              </label>
              <span className='m-form__titleTxt'>
                NURO 光：お客さま番号、NUROモバイル：ご契約番号
              </span>
              {errors.ispNumber && errorMessageComponent(errors.ispNumber.message)}
              <div className='m-form__lyt'>
                <input
                  type='text'
                  id='ispNumber'
                  className={`m-form-text ${errors.ispNumber && 'is-error'}`}
                  value={passwordResetForm.ispNumber}
                  disabled={passwordResetForm.simNumber !== ''}
                  {...register('ispNumber', {
                    onChange: (e) => {
                      updatePasswordResetFormInput(e);
                    },
                    onBlur: (e) => {
                      updatePasswordResetChangeFormInput(e);
                    },
                  })}
                />
              </div>
              <ul className='m-list-notice'>
                <li className='m-list-notice__item'>
                  NURO 光のお客さまはお客様番号の入力をお願いします。
                </li>
                <li className='m-list-notice__item'>
                  お客さま番号は契約内容通知書に記載しています。
                </li>
                <li className='m-list-notice__item'>
                  nuro、nuroM のいずれかの文字から始まる16けたの番号を入力してください。
                </li>
              </ul>
              <label htmlFor='simNumber' className='m-form__titleTxt'>
                SIM電話番号
              </label>
              {errors.simNumber && errorMessageComponent(errors.simNumber.message)}
              <div className='m-form__lyt'>
                <input
                  type='text'
                  id='simNumber'
                  className={`m-form-text ${errors.simNumber && 'is-error'}`}
                  value={passwordResetForm.simNumber}
                  maxLength={14}
                  disabled={passwordResetForm.ispNumber !== ''}
                  {...register('simNumber')}
                  onChange={updatePasswordResetFormInput}
                />
              </div>
              <ul className='m-list-notice'>
                <li className='m-list-notice__item'>
                  NUROモバイルのお客様はSIM電話番号の入力をお願いします。
                </li>
              </ul>
            </div>
          </div>
        )}
        <h2>注意事項</h2>
        <ul className='m-list-notice'>
          <li className='m-list-notice__item'>
            メールの「受信拒否設定」をされている方は、「＠nuro.jp」からのメールを受信できるように設定いただきますようお願いいたします。
          </li>
          <li className='m-list-notice__item'>ワンタイムパスワードは30分以内にご入力ください。</li>
          <li className='m-list-notice__item'>
            ワンタイムパスワードをご入力した際、エラーとなる場合は、再度ワンタイムパスワードの送信をお願いします。
          </li>
        </ul>
        <div className='m-button m-button--sec align-center'>
          <Button variant='secondary' onClick={handleSubmit(authenticate)}>
            次へ
          </Button>
        </div>
        <p className='m-linkTxt'>
          <ExternalLink href='https://support.nuro.jp/faqsupport/nuro/web/knowledge6985.html'>
            ユーザーIDの確認とパスワードの再設定方法を確認したい（NURO 光）
          </ExternalLink>
        </p>
        <p className='m-linkTxt'>
          <ExternalLink href='https://support.sonynetwork.co.jp/faqsupport/nuromobile/web/knowledge2736.html'>
            マイページのユーザーID・パスワードがわからない（NUROモバイル）
          </ExternalLink>
        </p>
      </main>
    </>
  );
};

export default PasswordReset;
